import { useQuery } from "@tanstack/react-query";
import { startTransition, useEffect } from "react";
import { CollapsibleState } from "~/components/build/Show/pages/steps/list/components/TreeItem/CollapsibleState";
import {
  TreeNode,
  TreeNodeRenderProps,
  TreeNodeType,
  createStepNode,
} from "~/components/build/Show/pages/steps/list/components/TreeNode";
import { CollapsibleStepItem } from "~/components/build/Show/pages/steps/list/components/TreeNode/CollapsibleStepItem";
import { FilterByOption, filterSteps, sortSteps } from "~/components/build/Show/lib/useFilterStore";
import { useBuild } from "~/components/build/Show/lib/BuildContext";
import { Build } from "~/components/build/Show/lib/types";
import { useBuildStore } from "~/components/build/Show/lib/useBuildStore";
import { GroupStep, Step } from "~/lib/pipeline";
import { childrenStepsQueryOptions } from "~/components/build/Show/lib/queries/steps";

const GroupStepItem = ({ step, ...props }: { step: GroupStep } & TreeNodeRenderProps) => {
  const { build } = useBuild();
  const setSteps = useBuildStore((state) => state.setSteps);
  const stepsGroupedByParent = useBuildStore((state) => state.stepsGroupedByParent);

  const query = useQuery(
    childrenStepsQueryOptions(build, step.uuid, {
      enabled:
        Features.ProgressiveBuildShow &&
        (props.collapsibleState === CollapsibleState.Expanded ||
          props.collapsibleState === CollapsibleState.Full),
    }),
  );

  useEffect(() => {
    if (query.data) {
      startTransition(() => {
        setSteps(query.data, build);
      });
    }
  }, [query.data]);

  // Show loading state when initial fetch is in progress and we haven't populated the store with all the children.
  const showLoadingState =
    query.isLoading &&
    stepsGroupedByParent.get(step.uuid)?.length !== step.statistics?.stepChildrenCount;

  return <CollapsibleStepItem {...props} step={step} isLoading={showLoadingState} />;
};

export const createGroupStepNode = (step: GroupStep, build: Build): TreeNode => ({
  id: step.uuid,
  type: TreeNodeType.StepGroup,
  render: (props) => <GroupStepItem step={step} {...props} />,
  childNodes: (collapsibleState: CollapsibleState, filters: FilterByOption[]) => {
    if (collapsibleState === CollapsibleState.Expanded) {
      const steps: Step[] = useBuildStore.getState().stepsGroupedByParent.get(step.uuid) || [];
      let filteredSteps = filterSteps(steps, filters, build);
      filteredSteps = sortSteps(filteredSteps);

      return filteredSteps.map((step) => createStepNode(step, build));
    }
    return [];
  },
});
